import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  ViewChild,
  forwardRef,
  OnInit,
  OnChanges,
  AfterViewChecked,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

@Component({
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.css'],
  imports: [CommonModule, FormsModule],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true,
    },
  ],
})
export class CustomDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewChecked
{
  @Input() options: Array<any> = [];
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() trackBy: string = '';
  @Input() multiSelectedLabel: string = 'Items selected';
  @Input() multiselectLabel: string = 'Select Items';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = false;
  @Input() multiselect: boolean = false;
  @Input() selectAllLabel: string = 'Select All';
  @Input() multiselecWithtApply: boolean = false;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() optionsOpen = new EventEmitter<boolean>();

  @ViewChild('dropdownMenu') dropdownMenuRef!: ElementRef;
  @ViewChild('container') container!: ElementRef;

  dropdownOpen = false;
  selected: any = null;
  private onChange: any = () => {};
  private onTouched: any = () => {};
  searchTerm: string = '';
  searchResults: Array<any> = [];
  selectAll: boolean = false;
  multiSelected: Array<any> = [];
  backupSelected: any = null;
  shouldScrollToSelected: boolean = false;

  constructor(private eRef: ElementRef) {}

  ngOnInit() {
    this.searchResults = this.options;
  }

  ngOnChanges(): void {
    this.onSearchChange();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToSelected && this.dropdownMenuRef?.nativeElement) {
      const selected = this.dropdownMenuRef.nativeElement.querySelector(
        'li.selected',
      ) as HTMLElement;
      if (selected) {
        const input = document.querySelector('.search-input');
        if (input instanceof HTMLInputElement) {
          input.focus();
        }
        setTimeout(() => {
          selected.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
      this.shouldScrollToSelected = false;
    }
  }

  onSelectAll(event: MouseEvent) {
    event.stopPropagation();
    this.selectAll = !this.selectAll;
    const disabledSelected = this.multiSelected.filter((opt) => opt.isDisabled);

    if (this.selectAll) {
      const enabledOptions = this.options.filter((opt) => !opt.isDisabled);
      this.multiSelected = [...disabledSelected, ...enabledOptions];
    } else {
      this.multiSelected = [...disabledSelected];
    }
    if (!this.multiselecWithtApply) {
      this.dropdownOpen = false;
      this.onChange(this.multiSelected);
      this.selectionChange.emit(this.multiSelected);
    }
  }

  onCancelClick(event: MouseEvent) {
    event.stopPropagation();
    this.dropdownOpen = false;
    this.multiSelected = [...this.backupSelected];
    this.selectAll = this.multiSelected.length === this.options.length;
  }

  onApplyClick(event: MouseEvent) {
    event.stopPropagation();
    this.dropdownOpen = false;
    this.onChange(this.multiSelected);
    this.selectionChange.emit(this.multiSelected);
  }

  getMultiselectLabel(): string {
    if (this.multiSelected.length === 0)
      return this.placeholder || this.multiselectLabel;
    if (this.multiSelected.length > 1) return this.multiSelectedLabel;
    return this.multiSelected[0][this.trackBy];
  }

  writeValue(value: any): void {
    if (this.multiselect) {
      if (Array.isArray(value)) {
        this.multiSelected = [...value];
        this.selectAll = this.multiSelected.length === this.options.length;
      } else {
        this.multiSelected = [];
      }
    } else {
      this.selected = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    this.optionsOpen.emit(this.dropdownOpen);
    if (this.dropdownOpen && this.multiselect)
      this.backupSelected = [...this.multiSelected];
    this.searchTerm = '';
    this.onSearchChange();
    if (this.dropdownOpen) {
      this.shouldScrollToSelected = true;
    }
  }
  onSearchChange() {
    const search = (this.searchTerm || '').toLowerCase();

    this.searchResults = this.options.filter((option) => {
      const val =
        option && option[this.trackBy] !== undefined
          ? String(option[this.trackBy])
          : '';
      return val.toLowerCase().includes(search);
    });
  }

  onEnterPress(): void {
    if (this.searchResults.length > 0) {
      const search = this.searchTerm.toLowerCase();
      const firstOption = this.options.find((opt) => {
        const val =
          opt && opt[this.trackBy] !== undefined
            ? String(opt[this.trackBy])
            : '';
        return val.toLowerCase() === search;
      });
      if (firstOption) this.onOptionClick(firstOption, new MouseEvent('click'));
    }
  }

  onOptionClick(option: any, event: MouseEvent): void {
    event.stopPropagation();
    if (option.isDisabled) return;
    if (this.multiselect) {
      const index = this.multiSelected.findIndex(
        (item) => item[this.trackBy] === option[this.trackBy],
      );
      if (index > -1) {
        this.multiSelected.splice(index, 1);
      } else {
        this.multiSelected.push(option);
      }
      this.selectAll = this.multiSelected.length === this.options.length;
      if (!this.multiselecWithtApply) {
        this.onChange(this.multiSelected);
        this.selectionChange.emit(this.multiSelected);
      }
    } else {
      this.selectOption(option);
    }
  }

  selectOption(option: any): void {
    this.selected = option;
    this.dropdownOpen = false;
    this.onChange(option); // Notify ngModel of the change
    this.selectionChange.emit(option);
  }

  getLabel(option: any): string {
    if (typeof option === 'string') return option;
    return this.trackBy && option?.[this.trackBy] !== undefined
      ? option[this.trackBy]
      : '[Unknown]';
  }

  isSelected(option: any): boolean {
    if (this.multiselect) {
      return this.multiSelected.some(
        (selected) => selected[this.trackBy] === option[this.trackBy],
      );
    }
    if (!this.selected) return false;
    return this.trackBy
      ? this.selected[this.trackBy] === option[this.trackBy]
      : this.selected === option;
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null) {
    if (!this.eRef.nativeElement.contains(target)) {
      this.dropdownOpen = false;
    }
  }
}
